import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSeo } from './hooks/useSeo';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { RefreshCw, LogOut, User, FileSpreadsheet, Building2, Plus, AlertTriangle, CheckCircle2, Sun, Moon, History, ChevronDown, ChevronRight, Wallet, Settings, ShieldCheck, FileDown, Download } from 'lucide-react';

import UploadZone from './components/UploadZone';
import ValidationErrors from './components/ValidationErrors';
import ExcelEditor from './components/ExcelEditor';
import GeneratePanel from './components/GeneratePanel';
import LettersList from './components/LettersList';
import CompanyProfileForm from './components/CompanyProfileForm';
import SignatoryProfileForm from './components/SignatoryProfileForm';
import LetterheadUpload from './components/LetterheadUpload';
import GenerationHistory from './components/GenerationHistory';
import BillingTab from './components/BillingTab';
import PaymentStatus from './components/PaymentStatus';
import AdminPanel from './components/AdminPanel';
import WagePanel from './components/WagePanel';
import Navbar from './components/Navbar';
import Home from './components/Home';
import About from './components/About';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import ScrollToHash from './components/ScrollToHash';

import { parseExcelFile, validateRows, COLUMN_MAP, downloadAppointmentTemplate } from './utils/excelParser';
import { useAuth } from './context/AuthContext';
import { validateExcelApi, authApi, profileApi, offerLetterApi, billingApi } from './utils/authApi';

import styles from './App.module.css';

const LOGO_SRC = 'data:image/x-icon;base64,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';

const sanitizeFilename = (name) => (name || '').trim().replace(/[^a-zA-Z0-9_-]/g, '_');

export default function App() {
  const { user, loading } = useAuth();
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #0d3b6e, #185FA5)'
      }}>
        <div style={{
          width: 40, height: 40, border: '3px solid rgba(255,255,255,0.3)',
          borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToHash />
      <Routes>
        <Route path="/" element={<><Navbar theme={theme} setTheme={setTheme} /><Home /></>} />
        <Route path="/about" element={<><Navbar theme={theme} setTheme={setTheme} /><About /></>} />
        <Route path="/terms" element={<><Navbar theme={theme} setTheme={setTheme} /><TermsPage /></>} />
        <Route path="/privacy" element={<><Navbar theme={theme} setTheme={setTheme} /><PrivacyPage /></>} />
        <Route path="/login" element={user ? <Navigate to="/app" replace /> : <><Navbar theme={theme} setTheme={setTheme} /><LoginPage /></>} />
        <Route path="/signup" element={user ? <Navigate to="/app" replace /> : <><Navbar theme={theme} setTheme={setTheme} /><SignupPage /></>} />
        <Route path="/payment-status" element={<><Navbar theme={theme} setTheme={setTheme} /><PaymentStatus /></>} />
        <Route path="/app" element={user ? <MainApp theme={theme} setTheme={setTheme} /> : <Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

function MainApp({ theme, setTheme }) {
  const { user, logout } = useAuth();
  // App states:
  // 'idle'       → upload zone shown
  // 'validating' → calling /validate-excel API
  // 'invalid'    → API returned errors, show ValidationErrors panel
  // 'previewing' → validation passed, show data preview + generate panel
  // 'generating' → generating letters
  // 'done'       → letters ready for download
  const [state, setState]             = useState('idle');
  const [rows, setRows]               = useState([]);
  const [filename, setFilename]       = useState('');
  const [warnings, setWarnings]       = useState([]);
  const [parseError, setParseError]   = useState('');
  const [genProgress, setGenProgress] = useState(0);
  const [generatedFiles, setGeneratedFiles] = useState([]);
  const [isZipping, setIsZipping]     = useState(false);
  const [format, setFormat]           = useState('both');
  const [validationResult, setValidationResult] = useState(null);
  const [letterheads, setLetterheads] = useState([]);
  const [selectedLetterheadId, setSelectedLetterheadId] = useState('');
  
  // Settings / Integration States
  const [activeTab, setActiveTab]     = useState('generator'); // 'generator', 'company_profile', 'company_signatory', 'company_letterhead', 'history'
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [subStatus, setSubStatus] = useState(null);

  const refreshSubscription = useCallback(() => {
    if (user) {
      billingApi.getSubscriptionStatus()
        .then(res => {
          setSubStatus(res);
          if (res && !res.is_docx_active) {
            setFormat('pdf');
          }
        })
        .catch(err => console.error("Failed to load subscription:", err));
    }
  }, [user]);

  useEffect(() => {
    refreshSubscription();
  }, [user, refreshSubscription]);

  useEffect(() => {
    if (user && activeTab === 'generator') {
      profileApi.listLetterheads()
        .then(list => {
          setLetterheads(list || []);
          if (list && list.length > 0) {
            const active = list.find(v => v.is_active);
            if (active) {
              setSelectedLetterheadId(active.id);
            } else {
              setSelectedLetterheadId(list[0].id);
            }
          }
        })
        .catch(err => console.error('Failed to list letterheads:', err));
    }
  }, [user, activeTab]);

  // ── SEO: authenticated main app ─────────────────────────────────────
  useSeo({
    title: 'Appointment Letter Generator',
    description: 'Generate compliant appointment letters from Excel data as DOCX and PDF on your company letterhead. Built for HR teams by CodeCrafters Inc.',
    noIndex: false,
  });

  const handleLogout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Backend logout failed:', err);
    }
    logout();
  }, [logout]);

  const handleCreateBlank = useCallback(() => {
    setParseError('');
    setGeneratedFiles([]);
    setValidationResult(null);
    setFilename('New_Employee_List.xlsx');
    
    const blankRow = {
      _rowIndex: 2,
      employeeName: '',
      dateOfBirth: '',
      parentName: '',
      aadhaarNumber: '',
      linNumber: '',
      uanEsic: '',
      designation: '',
      employmentType: '',
      skillCategory: '',
      dateOfJoining: '',
      basicPay: '',
      dearnessAllowance: '',
      otherAllowance: '',
      socialSecurity: '',
      duties: '',
      maternityBenefits: '',
      otherInfo: ''
    };
    
    setRows([blankRow]);
    setWarnings([]);
    setState('previewing');
  }, []);

  const handleFile = useCallback(async (file) => {
    setParseError('');
    setRows([]);
    setGeneratedFiles([]);
    setValidationResult(null);
    setFilename(file.name);
    setState('validating');

    try {
      // Step 1: Parse locally first so the grid editor always has data loaded
      const { rows: parsed } = await parseExcelFile(file);
      const warns = validateRows(parsed);
      setRows(parsed);
      setWarnings(warns);

      // Step 2: Server-side validation via /validate-excel API
      const result = await validateExcelApi(file, false);
      const normalizedResult = result.validation_result || result;
      setValidationResult(normalizedResult);

      if (!normalizedResult.success) {
        setState('invalid');
        return;
      }

      setState('previewing');
    } catch (err) {
      setParseError(err.message || 'Validation failed. Please try again.');
      setState('idle');
    }
  }, []);

  const handleRevalidate = useCallback(async (updatedRows) => {
    setParseError('');
    setValidationResult(null);

    try {
      // Dynamic import of xlsx just in case, but since it's already imported at top we can use XLSX directly
      // Re-map keys back to excel headers
      const reverseMap = {};
      for (const [excelCol, fieldKey] of Object.entries(COLUMN_MAP)) {
        reverseMap[fieldKey] = excelCol;
      }

      // Reconstruct raw data list
      const rawData = updatedRows.map(row => {
        const rawRow = {};
        for (const [fieldKey, val] of Object.entries(row)) {
          if (fieldKey === '_rowIndex') continue;
          const excelCol = reverseMap[fieldKey];
          if (excelCol) {
            rawRow[excelCol] = val;
          }
        }
        return rawRow;
      });

      // Write workbook in memory
      const worksheet = XLSX.utils.json_to_sheet(rawData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const fileBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const file = new File([fileBlob], filename || 'edited_employees.xlsx', { type: fileBlob.type });

      // Run server-side validation on edited file
      const result = await validateExcelApi(file, true);
      const normalizedResult = result.validation_result || result;
      setValidationResult(normalizedResult);

      // Validate warnings locally
      const warns = validateRows(updatedRows);
      setWarnings(warns);
      setRows(updatedRows);

      if (normalizedResult.success) {
        setState('previewing');
      } else {
        setState('invalid');
      }
    } catch (err) {
      setParseError(err.message || 'Validation failed. Please try again.');
      setState('invalid');
    }
  }, [filename]);

  const handleReupload = useCallback(() => {
    setState('idle');
    setRows([]);
    setFilename('');
    setValidationResult(null);
    setParseError('');
    setGeneratedFiles([]);
  }, []);

  const handleGenerate = useCallback(async () => {
    setParseError('');
    setState('generating');
    setGenProgress(0);
    setParseError('');

    try {
      await offerLetterApi.generateServer(selectedLetterheadId);
      
      // Setup polling interval
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await offerLetterApi.getStatus();
          const totalTarget = rows.length > 0 ? rows.length : (statusRes.total_employees || 0);
          const allEmployees = statusRes.employees || [];
          const readyCount = statusRes.ready_count || allEmployees.filter(emp => emp.ready).length;
          
          if (statusRes.status === 'failed' || statusRes.error) {
            clearInterval(pollInterval);
            setParseError(statusRes.error || 'Offer letter generation failed.');
            setState('previewing');
            return;
          }

          setGenProgress(Math.min(readyCount, totalTarget));

          if (readyCount >= totalTarget && totalTarget > 0) {
            clearInterval(pollInterval);
            const mappedFiles = allEmployees.map(emp => ({
              id: emp.employee_id,
              name: emp.employee_name,
              designation: '',
              safeName: sanitizeFilename(emp.employee_name),
              docxBlob: null,
              pdfBlob: null,
              docxFilename: `Appointment_${sanitizeFilename(emp.employee_name)}.docx`,
              pdfFilename: `Appointment_${sanitizeFilename(emp.employee_name)}.pdf`,
              serverReady: emp.ready,
            }));
            setGeneratedFiles(mappedFiles);
            setState('done');
            refreshSubscription();
          }
        } catch (pollErr) {
          console.error('Error polling offer letters status:', pollErr);
          clearInterval(pollInterval);
          setParseError(pollErr.message || 'Offer letter generation status polling failed.');
          setState('previewing');
        }
      }, 800); // Snappy 800ms polling for live updates!
    } catch (err) {
      setParseError(err.message || 'Failed to trigger server-side offer letter generation. Check profiles.');
      setState('previewing');
    }
  }, [rows, selectedLetterheadId, refreshSubscription]);

  const handleDownloadOne = useCallback(async (index, type) => {
    const f = generatedFiles[index];
    try {
      const blob = await offerLetterApi.downloadFile(f.id, type);
      saveAs(blob, type === 'docx' ? f.docxFilename : f.pdfFilename);
    } catch (err) {
      alert(`Failed to download file from server: ${err.message}`);
    }
  }, [generatedFiles]);

  const handleDownloadAll = useCallback(async (type) => {
    setIsZipping(true);
    try {
      const empIds = generatedFiles.map(f => f.id).filter(Boolean);
      const blob = await offerLetterApi.downloadZip(type, empIds.length > 0 ? empIds : null);
      saveAs(blob, `Appointment_Letters_${type === 'both' ? 'All' : type.toUpperCase()}.zip`);
    } catch (err) {
      alert(`Failed to download letters ZIP from server: ${err.message}`);
    } finally {
      setIsZipping(false);
    }
  }, [generatedFiles]);

  const handleReset = useCallback(() => {
    setState('idle');
    setRows([]); setFilename(''); setWarnings([]);
    setParseError(''); setGenProgress(0); setGeneratedFiles([]);
  }, []);

  const tabTitles = {
    generator: 'Appointment Letters',
    wages: 'Wage Slips (Form V)',
    company_profile: 'Company Profile Settings',
    company_signatory: 'Authorised Signatory Details',
    company_letterhead: 'Company Letterhead Template',
    history: 'Generation History Log',
    billing: 'Credit Balance & Pricing',
    admin_panel: 'Admin Panel',
  };

  return (
    <div className={styles.page}>
      {/* Sidebar Navigation */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarBrand}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className={styles.brandIcon}><img src={LOGO_SRC} alt="PaperlessBoss" /></div>
            <div>
              <div className={styles.brandName}>PaperlessBoss</div>
              <div className={styles.brandSub}>CodeCrafters Inc</div>
            </div>
          </div>
          
          {/* Mobile-only header controls */}
          <div className={styles.mobileHeaderControls}>
            <button 
              type="button" 
              className={styles.mobileActionBtn} 
              onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
              title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
            >
              {theme === 'light' ? <Moon size={14} /> : <Sun size={14} />}
            </button>
            <button 
              type="button" 
              className={`${styles.mobileActionBtn} ${styles.mobileLogoutBtn}`} 
              onClick={handleLogout} 
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>

        <nav className={styles.navGroup}>
          <button 
            type="button"
            className={`${styles.navItem} ${activeTab === 'generator' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('generator')}
          >
            <FileSpreadsheet size={16} />
            <span>Appointment Letters</span>
          </button>
          <button 
            type="button"
            className={`${styles.navItem} ${activeTab === 'wages' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('wages')}
          >
            <FileSpreadsheet size={16} />
            <span>Wage Slips</span>
          </button>
          <button 
            type="button"
            className={`${styles.navItem} ${['company_profile', 'company_signatory', 'company_letterhead'].includes(activeTab) ? styles.navItemActive : ''}`}
            onClick={() => {
              setIsCompanyDropdownOpen(prev => {
                const next = !prev;
                if (next && !['company_profile', 'company_signatory', 'company_letterhead'].includes(activeTab)) {
                  setActiveTab('company_profile');
                }
                return next;
              });
            }}
          >
            <Building2 size={16} />
            <span>Company Setup</span>
            {isCompanyDropdownOpen ? (
              <ChevronDown size={14} style={{ marginLeft: 'auto' }} />
            ) : (
              <ChevronRight size={14} style={{ marginLeft: 'auto' }} />
            )}
          </button>
          
          {isCompanyDropdownOpen && (
            <div className={styles.submenu}>
              <button
                type="button"
                className={`${styles.submenuItem} ${activeTab === 'company_profile' ? styles.submenuItemActive : ''}`}
                onClick={() => setActiveTab('company_profile')}
              >
                <span>Company Profile</span>
              </button>
              <button
                type="button"
                className={`${styles.submenuItem} ${activeTab === 'company_signatory' ? styles.submenuItemActive : ''}`}
                onClick={() => setActiveTab('company_signatory')}
              >
                <span>Authorised Signatory</span>
              </button>
              <button
                type="button"
                className={`${styles.submenuItem} ${activeTab === 'company_letterhead' ? styles.submenuItemActive : ''}`}
                onClick={() => setActiveTab('company_letterhead')}
              >
                <span>Letterhead PDF</span>
              </button>
            </div>
          )}
          <button 
            type="button"
            className={`${styles.navItem} ${activeTab === 'history' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <History size={16} />
            <span>Generation History</span>
          </button>
          <button 
            type="button"
            className={`${styles.navItem} ${activeTab === 'billing' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('billing')}
          >
            <Wallet size={16} />
             <span>Billing & Subscription</span>
          </button>
          {user?.email === 'admin@peperlessboss.com' && (
            <button 
              type="button"
              className={`${styles.navItem} ${activeTab === 'admin_panel' ? styles.navItemActive : ''}`}
              onClick={() => setActiveTab('admin_panel')}
            >
              <Settings size={16} />
              <span>Admin Panel</span>
            </button>
          )}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.sidebarUser} title={user?.email || 'User'} style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start', borderBottom: '1px solid var(--color-border)', paddingBottom: '10px', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <User size={14} style={{ flexShrink: 0 }} />
              <span className={styles.sidebarUserEmail} style={{ fontSize: '12px' }}>{user?.email || 'User'}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', fontWeight: 'bold', width: '100%' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                color: subStatus?.is_active ? '#10b981' : '#dc3545'
              }}>
                <ShieldCheck size={14} style={{ flexShrink: 0 }} />
                <span>{subStatus?.is_active ? `${subStatus.plan_name} Plan` : 'No Active Plan'}</span>
              </div>
              {subStatus?.is_active && (
                <div style={{ fontSize: '10px', color: 'var(--color-text-secondary)', fontWeight: 400, paddingLeft: '20px' }}>
                  {subStatus.days_remaining} days remaining
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <button 
              type="button" 
              className={styles.themeToggleBtn} 
              onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
              title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
            >
              {theme === 'light' ? <Moon size={14} /> : <Sun size={14} />}
              <span>{theme === 'light' ? 'Dark' : 'Light'}</span>
            </button>
            <button type="button" className={styles.sidebarLogoutBtn} onClick={handleLogout} title="Sign out">
              <LogOut size={14} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Workspace Content */}
      <div className={styles.mainContent}>
        <header className={styles.contentHeader}>
          <h1 className={styles.contentTitle}>{tabTitles[activeTab] || 'Dashboard'}</h1>
          {state !== 'idle' && activeTab === 'generator' && (
            <button className={styles.resetBtn} onClick={handleReset} aria-label="Start over">
              <RefreshCw size={13} />
              <span>Start Over</span>
            </button>
          )}
        </header>

        <main className={styles.workspace}>
          <div className={styles.container}>
            <div style={{ display: activeTab === 'company_profile' ? 'block' : 'none' }}>
              <CompanyProfileForm />
            </div>
            <div style={{ display: activeTab === 'company_signatory' ? 'block' : 'none' }}>
              <SignatoryProfileForm />
            </div>
            <div style={{ display: activeTab === 'company_letterhead' ? 'block' : 'none' }}>
              <LetterheadUpload active={activeTab === 'company_letterhead'} />
            </div>
            <div style={{ display: activeTab === 'history' ? 'block' : 'none' }}>
              <GenerationHistory active={activeTab === 'history'} hasDocxAddon={subStatus?.is_docx_active} />
            </div>
            <div style={{ display: activeTab === 'billing' ? 'block' : 'none' }}>
               <BillingTab />
            </div>
            {user?.email === 'admin@peperlessboss.com' && (
              <div style={{ display: activeTab === 'admin_panel' ? 'block' : 'none' }}>
                <AdminPanel />
              </div>
            )}

            <div style={{ display: activeTab === 'wages' ? 'block' : 'none' }}>
              <WagePanel />
            </div>

            <div style={{ display: activeTab === 'generator' ? 'block' : 'none' }}>
              <StepIndicator current={state} />

              {/* Upload or Create Blank Sheet option cards — shown when idle */}
              {state === 'idle' && (
                <div className={styles.selectionGrid}>
                  {/* Card 1: Upload Existing Excel */}
                  <UploadZone
                    onFileParsed={handleFile}
                    isParsing={false}
                    isValidating={false}
                    error={parseError}
                  />

                  {/* Card 2: Download Pre-Formatted Excel Template */}
                  <div 
                    className={styles.templateCard}
                    onClick={downloadAppointmentTemplate}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && downloadAppointmentTemplate()}
                  >
                    <div className={styles.templateIconWrap}>
                      <FileDown size={28} />
                    </div>
                    <h3 className={styles.templateTitle}>Download Excel Format</h3>
                    <p className={styles.templateText}>
                      Download the official pre-formatted Excel template with sample data and all 17 required columns.
                    </p>
                    <button type="button" className={styles.templateAction}>
                      <Download size={14} />
                      <span>Download Format (.xlsx)</span>
                    </button>
                  </div>

                  {/* Card 3: Start with Blank Sheet */}
                  <div 
                    className={styles.blankCard}
                    onClick={handleCreateBlank}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateBlank()}
                  >
                    <div className={styles.blankIconWrap}>
                      <Plus size={28} />
                    </div>
                    <h3 className={styles.blankTitle}>Start with Blank Sheet</h3>
                    <p className={styles.blankText}>
                      Write and edit employee details from scratch directly in the web editor without uploading.
                    </p>
                    <button type="button" className={styles.blankAction}>
                      <Plus size={14} />
                      <span>Create Blank Sheet</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Show Loading state when validating uploaded file */}
              {state === 'validating' && (
                <UploadZone
                  onFileParsed={handleFile}
                  isParsing={true}
                  isValidating={true}
                  error={parseError}
                />
              )}

              {/* Validation failed — show errors, block generation */}
              {state === 'invalid' && (
                <>
                  {validationResult ? (
                    <ValidationErrors
                      result={validationResult}
                      filename={filename}
                      onReupload={handleReupload}
                    />
                  ) : (
                    parseError && (
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#fde8e8',
                        border: '1px solid #f8b4b4',
                        borderRadius: '6px',
                        color: '#c53030',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}>
                        <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                        <span>{parseError}</span>
                      </div>
                    )
                  )}
                  <ExcelEditor 
                    rows={rows} 
                    setRows={setRows} 
                    filename={filename} 
                    warnings={warnings} 
                    validationResult={validationResult} 
                    onRevalidate={handleRevalidate} 
                  />
                </>
              )}

              {/* Validation passed — show preview + generate panel */}
              {(state === 'previewing' || state === 'generating') && (
                <>
                  {parseError && (
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#fde8e8',
                      border: '1px solid #f8b4b4',
                      borderRadius: '6px',
                      color: '#c53030',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}>
                      <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                      <span>{parseError}</span>
                    </div>
                  )}
                  {validationResult && validationResult.success && (
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#def7ec',
                      border: '1px solid #bcf0da',
                      borderRadius: '6px',
                      color: '#03543f',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}>
                      <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
                      <span>Successfully validated all the records</span>
                    </div>
                  )}
                  <ExcelEditor 
                    rows={rows} 
                    setRows={setRows} 
                    filename={filename} 
                    warnings={warnings} 
                    validationResult={validationResult} 
                    onRevalidate={handleRevalidate} 
                    isGenerating={state === 'generating'} 
                  />
                  {letterheads.length === 0 ? (
                    <div style={{
                      padding: '12px 16px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px dashed var(--color-gray-200)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      color: 'var(--color-gray-400)'
                    }}>
                      <span>ℹ️ <strong>No custom letterhead template found.</strong> Please upload a custom template under Company Setup, or the system will fall back to a basic plain layout.</span>
                    </div>
                  ) : (
                    <div className={styles.letterheadSelectorCard}>
                      <div className={styles.letterheadSelectorTitle}>
                        <span>Select Letterhead Template for Generation</span>
                      </div>
                      <select
                        className={styles.letterheadSelect}
                        value={selectedLetterheadId}
                        onChange={(e) => setSelectedLetterheadId(e.target.value)}
                        disabled={state === 'generating'}
                      >
                        {letterheads.map(lh => (
                          <option key={lh.id} value={lh.id}>
                            {lh.filename} {lh.is_active ? '(Active)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <GeneratePanel
                    total={rows.length} progress={genProgress}
                    isGenerating={state === 'generating'}
                    format={format} onFormatChange={setFormat} onGenerate={handleGenerate}
                    validationPassed={true}
                    hasDocxAddon={subStatus?.is_docx_active}
                  />
                </>
              )}

              {/* Letters generated — download panel */}
              {state === 'done' && (
                <>
                  <ExcelEditor 
                    rows={rows} 
                    setRows={setRows} 
                    filename={filename} 
                    warnings={warnings} 
                    validationResult={validationResult} 
                    onRevalidate={handleRevalidate} 
                    isReadOnly={true} 
                  />
                  <LettersList
                    files={generatedFiles} format={format}
                    onDownloadOne={handleDownloadOne} onDownloadAll={handleDownloadAll}
                    isZipping={isZipping}
                  />
                </>
              )}

              {state === 'idle' && <HelpCard />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

const STEPS = ['Upload', 'Validate', 'Preview', 'Generate', 'Download'];
const STATE_ORDER = { idle: 0, validating: 1, invalid: 1, previewing: 2, generating: 3, done: 4 };

function StepIndicator({ current }) {
  const ci = STATE_ORDER[current] ?? 0;
  return (
    <div className={styles.steps}>
      {STEPS.map((label, i) => {
        const done = i < ci, active = i === ci;
        return (
          <React.Fragment key={label}>
            <div className={`${styles.step} ${active ? styles.stepActive : ''} ${done ? styles.stepDone : ''}`}>
              <div className={styles.stepCircle}>{done ? '✓' : i + 1}</div>
              <span className={styles.stepLabel}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`${styles.stepLine} ${done ? styles.stepLineDone : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function HelpCard() {
  return (
    <div className={styles.helpCard}>
      <h3 className={styles.helpTitle}>How it works</h3>
      <ol className={styles.helpList}>
        <li>
          <strong>Prepare your Excel file</strong> — Use the official format with all 17 required employee columns.
          <button 
            type="button" 
            onClick={downloadAppointmentTemplate}
            className={styles.inlineTemplateBtn}
            title="Download Sample Excel Template"
          >
            <Download size={12} />
            <span>Download Format (.xlsx)</span>
          </button>
        </li>
        <li><strong>Upload the file</strong> — Drag &amp; drop or click to browse. Preview data before generating.</li>
        <li><strong>Choose format</strong> — Generate as DOCX, PDF, or both — all on the Company's or Vendor's letterhead.</li>
        <li><strong>Download</strong> — Individual files or all together as a .zip archive.</li>
      </ol>
      <div className={styles.helpColumns}>
        <div>
          <p className={styles.helpColTitle}>Required Excel columns</p>
          <ul className={styles.helpColList}>
            <li>Name of employee</li><li>Date of birth</li>
            <li>Father's / Mother's name</li><li>Aadhaar number</li>
            <li>Labour Identification Number (LIN)</li><li>Universal Account Number (UAN) / ESIC</li>
            <li>Designation</li><li>Type of Employment</li>
          </ul>
        </div>
        <div>
          <p className={styles.helpColTitle}>&nbsp;</p>
          <ul className={styles.helpColList}>
            <li>Category of Skill</li><li>Date of Joining</li>
            <li>Basic Pay</li><li>Dearness Allowance</li>
            <li>Other Allowance</li><li>Applicability of social security benefits</li>
            <li>Broad nature of duties performed</li><li>Maternity Benefit / Any other info</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
